import { z } from "zod";
import { ALLERGEN_CODES, LIMITS, PRODUCT_LABEL_CODES, TRANSLATION_LANGS } from "./constants";
import { isValidHours } from "./hours";
import { PRICE_ERROR_MESSAGES, parsePriceInput } from "./money";

// Request contracts for menu editing. Messages are the approved prototype texts.

const id = z.uuid("Identificator invalid.");

/** A user-typed price ("56,00") parsed into integer minor units with the prototype's rules. */
export const priceInput = z.string("Introdu prețul.").transform((value, ctx) => {
  const r = parsePriceInput(value);
  if (r.ok) return r.minor;
  ctx.issues.push({ code: "custom", message: PRICE_ERROR_MESSAGES[r.error], input: value });
  return z.NEVER;
});

const uniqueList = <T extends string>(item: z.ZodType<T>, max: number) =>
  z
    .array(item)
    .max(max)
    .refine((xs) => new Set(xs).size === xs.length, "Elementele nu se pot repeta.");

/** A menu's display schedule (daily / scheduled menus); "" = always shown. */
export const menuScheduleSchema = z.object({
  schedule: z
    .string()
    .trim()
    .max(400)
    .refine(isValidHours, "Verifică programul: orele au forma 12:00, iar ora de închidere e diferită."),
});

export const menuNameSchema = z.object({
  name: z.string().trim().min(1, "Adaugă un nume pentru meniu.").max(80, "Numele este prea lung."),
});

const translationLang = z.enum(TRANSLATION_LANGS);

export const categorySchema = z.object({
  name: z.string().trim().min(1, "Adaugă numele categoriei.").max(80, "Numele este prea lung."),
  /** Optional translated name; an empty name removes the translation. */
  translation: z
    .object({ lang: translationLang, name: z.string().trim().max(80, "Numele este prea lung.") })
    .optional(),
});
export type CategoryInput = z.infer<typeof categorySchema>;

export const reorderSchema = z.object({ order: z.array(id).min(1).max(LIMITS.categoriesPerMenu) });

export const categoryDeleteSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("move"), targetCategoryId: id }),
  z.object({ mode: z.literal("delete") }),
]);
export type CategoryDeleteInput = z.infer<typeof categoryDeleteSchema>;

const variantInput = z.object({
  label: z
    .string()
    .trim()
    .min(1, "Adaugă eticheta variantei, de ex. 32 cm.")
    .max(40, "Eticheta este prea lungă."),
  price: priceInput,
});

export const productSchema = z.object({
  categoryId: z.uuid("Alege o categorie."),
  name: z.string().trim().min(1, "Adaugă numele produsului.").max(150, "Numele este prea lung."),
  description: z.string().trim().max(1000, "Descrierea este prea lungă.").default(""),
  ingredients: z.string().trim().max(1000, "Lista de ingrediente este prea lungă.").default(""),
  portion: z.string().trim().max(40, "Porția este prea lungă.").default(""),
  pricing: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("single"), price: priceInput }),
    z.object({
      mode: z.literal("variants"),
      variants: z
        .array(variantInput)
        .min(2, "Adaugă cel puțin două variante sau folosește un preț unic.")
        .max(LIMITS.variantsPerProduct, "Prea multe variante."),
    }),
  ]),
  allergens: uniqueList(z.enum(ALLERGEN_CODES), ALLERGEN_CODES.length).default([]),
  /** The restaurant explicitly confirms the allergen list (including "none declared"). Never inferred. */
  allergensConfirmed: z.boolean().default(false),
  labels: uniqueList(z.enum(PRODUCT_LABEL_CODES), PRODUCT_LABEL_CODES.length).default([]),
  /** Made from a frozen product: set by the restaurant, shown in the public menu. */
  frozen: z.boolean().default(false),
  /** Uploaded photo (`media.id`). Omitted: keep the current photo; null: remove it. */
  imageId: z.uuid("Fotografia nu mai este disponibilă. Încarc-o din nou.").nullable().optional(),
  /** Optional translation edited together with the product (prototype editor). Empty name removes it. */
  translation: z
    .object({
      lang: translationLang,
      name: z.string().trim().max(150, "Numele este prea lung."),
      description: z.string().trim().max(1000, "Descrierea este prea lungă.").default(""),
      ingredients: z.string().trim().max(1000, "Lista de ingrediente este prea lungă.").default(""),
      reviewed: z.boolean().default(false),
    })
    .optional(),
});
export type ProductInput = z.infer<typeof productSchema>;

export const priceUpdateSchema = z.object({ price: priceInput });
export const frozenUpdateSchema = z.object({ frozen: z.boolean("Alege da sau nu.") });

/** "Gramaj de verificat": the portion the restaurant confirms (as printed, total only, or corrected). */
export const portionResolutionSchema = z.object({
  portion: z.string().trim().max(40, "Porția este prea lungă."),
});

/** "Nume de verificat": the name as it is on the menu (one of the readings, or typed). */
export const nameResolutionSchema = z.object({
  name: z.string().trim().min(1, "Scrie numele produsului.").max(150, "Numele este prea lung."),
});

/** Publish / discard confirm the draft revision the user previewed. */
export const draftRevisionSchema = z.object({
  draftRevision: z.number("Revizia ciornei lipsește.").int().min(0),
});

/** Publish request: the revision previewed and, when products lack allergen data, the explicit notice. */
export const publishSchema = draftRevisionSchema.extend({ allergenNotice: z.boolean().optional() });

export const availabilitySchema = z.object({ available: z.boolean("Alege disponibilitatea.") });

/** The owner's decision about a menu language: offered on the public page or withdrawn. */
export const menuLanguageSchema = z.object({
  status: z.enum(["approved", "hidden"], "Alege starea limbii."),
});
